/**
 * FeaturePanel Component
 * 
 * Reusable panel for the feature showcase section.
 * Supports alternating layout (visual left/right) and blueprint placeholders.
 */

import React from 'react';
import { motion } from 'framer-motion';

const FeaturePanel = ({
  id,
  eyebrow,
  headline,
  description,
  bullets = [],
  tagline = null, // Bold statement after bullets
  primaryCta,
  secondaryCta,
  visualPosition = 'right', // 'left' or 'right'
  visualPlaceholder = true,
  visualContent = null, // For future: actual visual component
  visualCaption = null, // { text, showCta, ctaLabel, ctaHref, showMissionLink }
  className = '',
}) => {
  const isVisualLeft = visualPosition === 'left';

  // Handle CTA click - smooth scroll for anchor links
  const handleCtaClick = (href) => {
    if (href && href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Scroll to mission statement
  const scrollToMission = () => {
    const section = document.getElementById('mission-statement');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const textVariants = {
    hidden: { opacity: 0, x: isVisualLeft ? 30 : -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  const visualVariants = {
    hidden: { opacity: 0, x: isVisualLeft ? -30 : 30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: 'easeOut', delay: 0.3 },
    },
  };

  const bulletVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  // Text content block
  const TextContent = (
    <motion.div 
      className="feature-panel__text"
      variants={textVariants}
    >
      <span className="feature-panel__eyebrow">{eyebrow}</span>
      <h2 className="feature-panel__headline">{headline}</h2>
      <p className="feature-panel__description">{description}</p>
      
      {bullets.length > 0 && (
        <motion.ul 
          className="feature-panel__bullets"
          variants={containerVariants}
        >
          {bullets.map((bullet, index) => (
            <motion.li 
              key={index} 
              className="feature-panel__bullet"
              variants={bulletVariants}
            >
              <span className="feature-panel__bullet-dot" aria-hidden="true" />
              {bullet}
            </motion.li>
          ))}
        </motion.ul>
      )}

      {tagline && (
        <motion.p 
          className="feature-panel__tagline"
          variants={bulletVariants}
        >
          {tagline}
        </motion.p>
      )}

      <div className="feature-panel__cta-row">
        {primaryCta && (
          <a 
            href={primaryCta.href || '#'} 
            className="feature-panel__cta feature-panel__cta--primary"
          >
            {primaryCta.label}
          </a>
        )}
        {secondaryCta && (
          <a 
            href={secondaryCta.href || '#'} 
            className="feature-panel__cta feature-panel__cta--ghost"
          >
            {secondaryCta.label}
          </a>
        )}
      </div>
    </motion.div>
  );

  // Visual content block (placeholder or actual)
  const VisualContent = (
    <motion.div 
      className="feature-panel__visual-wrapper"
      variants={visualVariants}
    >
      <div className="feature-panel__visual">
        {visualPlaceholder && !visualContent ? (
          <div className="feature-panel__placeholder">
            <div className="feature-panel__placeholder-grid">
              {/* Animated corner brackets */}
              <span className="feature-panel__placeholder-corner feature-panel__placeholder-corner--tl" />
              <span className="feature-panel__placeholder-corner feature-panel__placeholder-corner--tr" />
              <span className="feature-panel__placeholder-corner feature-panel__placeholder-corner--bl" />
              <span className="feature-panel__placeholder-corner feature-panel__placeholder-corner--br" />
              
              {/* Center content */}
              <div className="feature-panel__placeholder-content">
                <div className="feature-panel__placeholder-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="6" y="6" width="36" height="36" rx="4" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" />
                    <path d="M18 24h12M24 18v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
                <span className="feature-panel__placeholder-label">Visual coming soon</span>
              </div>
            </div>
          </div>
        ) : (
          visualContent
        )}
      </div>

      {visualCaption && (
        <div className="feature-panel__visual-caption">
          <p className="feature-panel__caption-text">{visualCaption.text}</p>
          {visualCaption.showCta && (
            <div className="feature-panel__caption-cta-row">
              <button 
                className="ea-cta-button feature-panel__caption-cta"
                onClick={() => handleCtaClick(visualCaption.ctaHref || '#early-access')}
              >
                {visualCaption.ctaLabel || 'Register Interest'}
                <span className="ea-arrow">→</span>
              </button>
              {visualCaption.showMissionLink !== false && (
                <button className="ea-mission-link" onClick={scrollToMission}>
                  Read Mission Statement
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );

  return (
    <motion.article
      id={id}
      className={`feature-panel ${isVisualLeft ? 'feature-panel--visual-left' : ''} ${className}`.trim()}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-100px' }}
      variants={containerVariants}
    >
      <div className="feature-panel__container">
        {isVisualLeft ? (
          <>
            {VisualContent}
            {TextContent}
          </>
        ) : (
          <>
            {TextContent}
            {VisualContent}
          </>
        )}
      </div>
    </motion.article>
  );
};

export default FeaturePanel;
